import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type SoteriaCardVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'destructive';

// Variant colors the ICON only. A tinted, colored-border card per result reads
// as a marketing callout, and a turn that produces three of them becomes a wall
// of colored boxes. Premium assistant surfaces keep result cards on the neutral
// card surface with a hairline border and spend color on one small glyph.
const ICON_TONE: Record<SoteriaCardVariant, string> = {
  default: 'text-primary',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

/**
 * Shared CTA class so every card's action reads identically — a quiet
 * secondary control, not a filled brand pill. The card is already the
 * affordance; a saturated button on top of it competes with the reply.
 */
export function soteriaCardCtaClass(
  _variant: SoteriaCardVariant = 'default',
): string {
  return cn(
    'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border',
    'bg-background/60 px-2.5 py-1 text-xs font-medium text-foreground',
    'transition-colors hover:bg-muted',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
  );
}

interface SoteriaCardProps {
  variant?: SoteriaCardVariant;
  /** The lucide icon element — pass it WITHOUT a color class; the card colors it. */
  icon: ReactNode;
  /** Optional overlay on the icon (e.g. a spinner badge). */
  iconOverlay?: ReactNode;
  eyebrow: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  cta?: ReactNode;
  children?: ReactNode;
  className?: string;
}

/**
 * The single card chrome for every Soteria generative result (strategy
 * created, backtest, report, client, navigation, account draft).
 *
 * Restrained on purpose: neutral `bg-card` surface, hairline border, no tint
 * wash and no icon tile. The eyebrow is muted small-caps metadata rather than a
 * colored label, so several cards in one turn stack calmly instead of
 * competing. All colors are tokens, so it adapts to dark mode.
 */
export function SoteriaCard({
  variant = 'default',
  icon,
  iconOverlay,
  eyebrow,
  title,
  subtitle,
  cta,
  children,
  className,
}: SoteriaCardProps) {
  return (
    <div
      className={cn(
        'mt-3 rounded-xl border border-border bg-card px-4 py-3.5',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className={cn('relative mt-0.5 shrink-0', ICON_TONE[variant])}>
          {icon}
          {iconOverlay}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
            {eyebrow}
          </p>
          {title != null && (
            <p className="mt-1 truncate text-sm font-medium text-foreground">
              {title}
            </p>
          )}
          {subtitle != null && (
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              {subtitle}
            </p>
          )}
        </div>
        {cta}
      </div>
      {children}
    </div>
  );
}
